import React from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { useNavigate } from 'react-router-dom'

import config from '../../config'
import {
  calcIsUserLoggedIn as calcIsUserLoggedIn2,
  ensureFunnelUserEnriched,
} from '../../redux/services/auth'
import { setSentryUserContext } from '../../sentry/userContext'

// Utils
import { logError } from '../../utils/logger'
import {
  checkNotificationFeature,
  askNotificationPermission,
  PERMISSION_DEFAULT,
  PERMISSION_DENIED,
  PERMISSION_GRANTED,
} from '../../utils/Notifications'
import { getDefaultSyncDateRangeISOs } from '../../server/utils/date'
import { shouldShowNotificationNow } from './utils'

// Constants
import { initialReminderSettings } from '../../App/constants'

// Sync Functions
import { syncUserSettings, syncOutlookEvents, syncOutlookEmails } from './syncFunc'

import queryClient from '../../queryClient'
import {
  OUTLOOK_EVENTS_QUERY_KEY,
  OUTLOOK_EMAILS_QUERY_KEY,
  REMINDER_SETTINGS_QUERY_KEY,
} from '../../hooks/queries/keys'

const { IS_WEB } = config

class DataSyncClass extends React.Component {
  reminderInterval = null

  componentDidMount = async () => {
    this.props.watchConnectivity()
    this.reminderInterval = setInterval(this.reminderTimerFunction, 1 * 60 * 1000)

    if (this.props.io.socketHandlers.eventsUpdated === null) {
      this.props.io.socketHandlers.eventsUpdated = () => this.handleEventsUpdatedBySocket()
    }
    if (this.props.io.socketHandlers.emailsUpdated === null) {
      this.props.io.socketHandlers.emailsUpdated = () => this.handleEmailsUpdatedBySocket()
    }

    const reloadSession = async () => {
      try {
        const { isLoggedIn, userInfo } = await calcIsUserLoggedIn2(this.props.auth0)
        if (isLoggedIn) {
          let userInfoToStore = userInfo
          try {
            const merged = await ensureFunnelUserEnriched(this.props.client, this.props.auth0)
            userInfoToStore = { ...merged, picture: userInfo.picture }
          } catch (enrichErr) {
            logError(enrichErr, 'DataSync > reloadSession > ensureFunnelUserEnriched')
          }
          this.props.loginSuccess(userInfoToStore)
          await setSentryUserContext(userInfoToStore, { client: this.props.client })
          return { isUserLoggedIn: true, isRehidrate: true }
        }
        return {}
      } catch (e) {
        return {}
      }
    }

    const { isUserLoggedIn = false, isRehidrate } = await reloadSession()

    console.log('DataSync > componentDidMount', { isUserLoggedIn, isRehidrate })

    if (isUserLoggedIn) {
      if (isRehidrate) {
        this.props.setIsTimesheetLoading(true)

        try {
          const selectedDelegateId = window
            ? window.localStorage.getItem('selectedDelegateId')
            : _.get(this.props, 'selectedDelegateId', null)

          if (selectedDelegateId) {
            this.props.handleSelectedDelegateId(selectedDelegateId)
          }

          const syncData = {
            client: this.props.client,
            selectedDelegateId,
            getUIRefreshTimePeriod: this.props.getUIRefreshTimePeriod,
            handleAppState: this.props.handleAppState,
          }

          await syncUserSettings()
          this.props.setInitialCurrentEditableEntry()

          const { startISO, endISO } = getDefaultSyncDateRangeISOs()
          await syncOutlookEvents({
            ...syncData,
            startISO,
            endISO,
            isDelta: true,
            reSyncDelta: true,
          })
          await syncOutlookEmails({
            ...syncData,
            startISO,
            endISO,
            isDelta: true,
            reSyncDelta: true,
          })

          await this.props.startInitialDelegateAccessSync()
        } catch (e) {
          logError(e)
        } finally {
          this.props.setIsTimesheetLoading(false)
        }
      } else {
        // Go directly to Calendar instead of Welcome Screen
        this.props.navigate('/')
        this.props.navigate('/', { replace: true })
      }
    }
  }

  componentWillUnmount = () => {
    clearInterval(this.reminderInterval)
  }

  componentDidUpdate = async prevProps => {
    const { isUserLoggedIn, isConnected } = this.props

    // LISTENS TO LOGOUT
    if (!isUserLoggedIn && prevProps.isUserLoggedIn) {
      this.props.setToInitialState()
    }

    // LISTENS TO CONNECTION CHANGE
    if (isConnected && !prevProps.isConnected) {
      this.props.updateViewData()
    }
  }

  handleEventsUpdatedBySocket = () => {
    queryClient.invalidateQueries(OUTLOOK_EVENTS_QUERY_KEY)
  }

  handleEmailsUpdatedBySocket = () => {
    queryClient.invalidateQueries(OUTLOOK_EMAILS_QUERY_KEY)
  }

  reminderTimerFunction = async () => {
    const reminderSettings =
      queryClient.getQueryData(REMINDER_SETTINGS_QUERY_KEY) ?? initialReminderSettings
    if (!shouldShowNotificationNow(reminderSettings)) return

    if (!IS_WEB) return window.displayNotification("Don't forget to track time!")

    if (!checkNotificationFeature() || Notification.permission === PERMISSION_DENIED) return

    if (Notification.permission === PERMISSION_DEFAULT) {
      await askNotificationPermission()
      if (Notification.permission !== PERMISSION_GRANTED) return
    }

    const text = "Don't forget to track time!"

    const notification = new Notification('Glimpse', { body: text })

    return notification
  }

  render() {
    return null
  }
}

DataSyncClass.propTypes = {
  hasUserGotStarted: PropTypes.bool,
  isConnected: PropTypes.bool.isRequired,
  auth0: PropTypes.object.isRequired,
  handleAppState: PropTypes.func.isRequired,
  getUIRefreshTimePeriod: PropTypes.func.isRequired,
  client: PropTypes.object.isRequired,
  io: PropTypes.object.isRequired,
  loginSuccess: PropTypes.func.isRequired,
  isUserLoggedIn: PropTypes.bool.isRequired,
  setToInitialState: PropTypes.func.isRequired,
  updateViewData: PropTypes.func.isRequired,
  selectedDelegateId: PropTypes.string,
  isRecurrentSyncInProgress: PropTypes.bool,
  setInitialCurrentEditableEntry: PropTypes.func.isRequired,
  handleSelectedDelegateId: PropTypes.func.isRequired,
  startInitialDelegateAccessSync: PropTypes.func.isRequired,
  setIsTimesheetLoading: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  watchConnectivity: PropTypes.func.isRequired,
}

// Wrapper for useNavigate hook.
// Todo: MIGRATE TO FN COMPONENT INSTEAD
const DataSync = props => {
  const navigate = useNavigate()
  return <DataSyncClass navigate={navigate} {...props} />
}

export { DataSyncClass }

export default DataSync
